import { z } from 'zod';
import rawFalModels from '../data/fal-models.json';
import { jobKindSchema, type JobKind } from '../schemas/job';

const pricingTierSchema = z.object({
  price: z.number().nonnegative(),
  currency: z.string().default('USD'),
  unit: z.string(),
  metric: z.string(),
  priceText: z.string().optional(),
  description: z.string().optional()
});

const retailPricingTierSchema = pricingTierSchema.extend({
  credits: z.number().int().nonnegative(),
  minimumCredits: z.number().int().positive().optional()
});

const pricingVariantSchema = pricingTierSchema.extend({
  criteria: z
    .array(
      z.object({
        key: z.string().optional(),
        title: z.string().optional(),
        description: z.string().optional(),
        type: z.string().optional(),
        subtype: z.string().optional(),
        value: z.union([z.string(), z.number(), z.boolean()]).optional()
      })
    )
    .optional(),
  retailPrice: z.number().nonnegative().optional(),
  retailCredits: z.number().int().nonnegative().optional()
});

const modelSettingsSchema = z
  .object({
    required: z.array(z.string()).optional(),
    properties: z.record(z.string(), z.unknown()).optional()
  })
  .optional();

const modelUiSchema = z
  .object({
    hiddenSettings: z.array(z.string()).optional(),
    relaxedDefaults: z.record(z.string(), z.unknown()).optional(),
    labelOverrides: z.record(z.string(), z.string()).optional(),
    optionLabelOverrides: z.record(z.string(), z.record(z.string(), z.string())).optional()
  })
  .optional();

const endpointConditionSchema = z.object({
  type: z.enum([
    'default',
    'asset_present',
    'asset_missing',
    'setting_present',
    'setting_absent',
    'setting_equals'
  ]),
  assetFields: z.array(z.string()).optional(),
  minAssetCount: z.number().int().nonnegative().optional(),
  settingKey: z.string().optional(),
  value: z.union([z.string(), z.number(), z.boolean()]).optional()
});

const endpointSupportsSchema = z
  .object({
    multiOutput: z.boolean().optional(),
    maxOutputs: z.number().int().positive().optional(),
    referenceImages: z.boolean().optional(),
    referenceVideos: z.boolean().optional(),
    multiOutputSettingKey: z.string().optional()
  })
  .optional();

const endpointSchema = z.object({
  key: z.string(),
  endpointId: z.string(),
  label: z.string(),
  description: z.string().optional(),
  defaultSettings: z.record(z.string(), z.unknown()).optional(),
  pricing: z
    .object({
      provider: pricingTierSchema,
      retail: retailPricingTierSchema,
      variants: z.array(pricingVariantSchema).optional(),
      notes: z.string().optional()
    })
    .optional(),
  settings: modelSettingsSchema,
  ui: modelUiSchema,
  output: z
    .object({
      type: z.enum(['image', 'video', 'audio'])
    })
    .optional(),
  supports: endpointSupportsSchema,
  selectWhen: z.array(endpointConditionSchema).optional(),
  priority: z.number().int().optional()
});

const fallbackProviderSchema = z.object({
  provider: z.enum(['replicate', 'fal']),
  modelId: z.string(),
  endpointId: z.string().optional(),
  reason: z.string().optional(),
  notes: z.string().optional()
});

const falModelSchema = z.object({
  id: z.string(),
  endpointId: z.string(),
  owner: z.string().default('fal-ai'),
  model: z.string().optional(),
  label: z.string(),
  description: z.string().optional(),
  kind: jobKindSchema,
  category: z.string().optional(),
  defaultSettings: z.record(z.string(), z.unknown()).default({}),
  aliases: z.array(z.string()).optional(),
  pricing: z.object({
    provider: pricingTierSchema,
    retail: retailPricingTierSchema,
    variants: z.array(pricingVariantSchema).optional(),
    notes: z.string().optional()
  }),
  settings: modelSettingsSchema,
  ui: modelUiSchema,
  defaultEndpointKey: z.string().optional(),
  endpoints: z.array(endpointSchema).optional(),
  fallbackProviders: z.array(fallbackProviderSchema).optional(),
  rateLimitKey: z.string().optional(),
  output: z.object({
    type: z.enum(['image', 'video', 'audio'])
  })
});

type RawFalModel = z.infer<typeof falModelSchema>;
type RawFalEndpoint = z.infer<typeof endpointSchema>;
type PricingTier = z.infer<typeof pricingTierSchema>;
type RetailPricingTier = z.infer<typeof retailPricingTierSchema>;
type PricingVariant = z.infer<typeof pricingVariantSchema>;
type ModelUiConfig = z.infer<typeof modelUiSchema>;

export type FalEndpointCondition = z.infer<typeof endpointConditionSchema>;
export type FalEndpointSupports = z.infer<typeof endpointSupportsSchema>;
export type FalEndpointPricing = {
  provider: PricingTier;
  retail: RetailPricingTier;
  variants?: PricingVariant[];
  notes?: string;
};
export type FalEndpointConfig = {
  key: string;
  endpointId: string;
  label: string;
  description?: string;
  defaultSettings: Record<string, unknown>;
  pricing: FalEndpointPricing;
  settings?: {
    required?: string[];
    properties?: Record<string, unknown>;
  };
  ui?: ModelUiConfig;
  output: {
    type: 'image' | 'video' | 'audio';
  };
  supports?: FalEndpointSupports;
  selectWhen: FalEndpointCondition[];
  priority?: number;
};
export type FalFallbackProviderConfig = z.infer<typeof fallbackProviderSchema>;

export type FalModelConfig = {
  id: string;
  endpointId: string;
  owner: string;
  model: string;
  aliases?: string[];
  label: string;
  description?: string;
  kind: JobKind;
  category?: string;
  defaultSettings: Record<string, unknown>;
  pricing: {
    provider: PricingTier;
    retail: RetailPricingTier;
    variants?: PricingVariant[];
    notes?: string;
  };
  settings?: {
    required?: string[];
    properties?: Record<string, unknown>;
  };
  ui?: ModelUiConfig;
  rateLimitKey?: string;
  output: {
    type: 'image' | 'video' | 'audio';
  };
  provider: 'fal';
  defaultEndpointKey: string;
  endpoints: FalEndpointConfig[];
  fallbackProviders?: FalFallbackProviderConfig[];
};

const RAW_MODELS: RawFalModel[] = falModelSchema.array().parse(rawFalModels);

const ensureSelectWhen = (conditions?: FalEndpointCondition[]): FalEndpointCondition[] => {
  if (!conditions || !conditions.length) {
    return [{ type: 'default' }];
  }
  return conditions;
};

const mapFalEndpoint = (
  model: RawFalModel,
  endpoint: RawFalEndpoint,
  defaults: {
    defaultSettings: Record<string, unknown>;
    settings?: RawFalModel['settings'];
    ui?: RawFalModel['ui'];
  }
): FalEndpointConfig => {
  const pricingSource = endpoint.pricing ?? model.pricing;
  const defaultSettings = {
    ...defaults.defaultSettings,
    ...(endpoint.defaultSettings ?? {})
  };

  return {
    key: endpoint.key,
    endpointId: endpoint.endpointId,
    label: endpoint.label,
    description: endpoint.description ?? model.description,
    defaultSettings,
    pricing: {
      provider: pricingSource.provider,
      retail: pricingSource.retail,
      variants: pricingSource.variants,
      notes: pricingSource.notes
    },
    settings: endpoint.settings ?? defaults.settings,
    ui: endpoint.ui ?? defaults.ui,
    output: endpoint.output ?? model.output,
    supports: endpoint.supports ?? undefined,
    selectWhen: ensureSelectWhen(endpoint.selectWhen as FalEndpointCondition[] | undefined),
    priority: endpoint.priority
  };
};

const mapFalModel = (model: RawFalModel): FalModelConfig => {
  const endpointParts = model.endpointId.split('/');
  const fallbackOwner = endpointParts.length > 1 ? endpointParts[0] : 'fal-ai';
  const fallbackModel = endpointParts.length > 1 ? endpointParts.slice(1).join('/') : model.endpointId;

  const defaultSettings = {
    ...(model.defaultSettings ?? {})
  };
  const defaultNumImages = defaultSettings.num_images;
  if (
    defaultSettings.num_outputs === undefined &&
    typeof defaultNumImages === 'number' &&
    Number.isFinite(defaultNumImages)
  ) {
    defaultSettings.num_outputs = defaultNumImages;
  }

  const originalSettings = model.settings;
  const originalProperties =
    (originalSettings?.properties as Record<string, unknown> | undefined) ?? undefined;
  const properties = originalProperties ? { ...originalProperties } : undefined;

  if (properties && !('num_outputs' in properties) && properties.num_images) {
    const numImagesSchema = properties.num_images as Record<string, unknown>;
    const numImagesConfig = numImagesSchema as { title?: unknown };
    const numImagesTitle =
      typeof numImagesConfig.title === 'string' ? (numImagesConfig.title as string) : undefined;
    properties.num_outputs = {
      ...numImagesSchema,
      title: numImagesTitle ?? 'Outputs'
    };
  }

  const settings =
    properties !== undefined
      ? {
          ...(originalSettings ?? {}),
          properties
        }
      : originalSettings;

  const hiddenSettingsSet = new Set(model.ui?.hiddenSettings ?? []);
  if (properties?.num_images) {
    hiddenSettingsSet.add('num_images');
  }

  const existingLabelOverrides = model.ui?.labelOverrides ?? {};
  const labelOverrides: Record<string, string> = { ...existingLabelOverrides };
  if (!labelOverrides.num_outputs && properties?.num_images) {
    const numImagesSchema = properties.num_images as Record<string, unknown>;
    const numImagesConfig = numImagesSchema as { title?: unknown };
    const fallbackLabel =
      typeof existingLabelOverrides.num_images === 'string'
        ? existingLabelOverrides.num_images
        : (typeof numImagesConfig.title === 'string' ? (numImagesConfig.title as string) : undefined);
    if (fallbackLabel) {
      labelOverrides.num_outputs = fallbackLabel;
    }
  }

  const hiddenSettingsArray = hiddenSettingsSet.size ? Array.from(hiddenSettingsSet) : undefined;

  let ui = model.ui ? { ...model.ui } : undefined;
  if (hiddenSettingsArray) {
    ui = {
      ...(ui ?? {}),
      hiddenSettings: hiddenSettingsArray
    };
  }
  if (Object.keys(labelOverrides).length) {
    ui = {
      ...(ui ?? {}),
      labelOverrides
    };
  }

  const rawEndpoints =
    Array.isArray(model.endpoints) && model.endpoints.length
      ? (model.endpoints as RawFalEndpoint[])
      : [
          {
            key: 'default',
            endpointId: model.endpointId,
            label: model.label,
            description: model.description,
            defaultSettings: model.defaultSettings,
            pricing: model.pricing,
            settings: model.settings,
            ui: model.ui,
            output: model.output,
            selectWhen: [{ type: 'default' as const }]
          }
        ];

  const endpoints = rawEndpoints.map((endpoint) =>
    mapFalEndpoint(model, endpoint, {
      defaultSettings,
      settings,
      ui
    })
  );

  const defaultEndpointKey = model.defaultEndpointKey ?? endpoints[0]?.key ?? 'default';

  const fallbackProviders = model.fallbackProviders
    ? model.fallbackProviders.map((provider) => ({ ...provider }))
    : [];

  const aliasSet = new Set(model.aliases ?? []);
  aliasSet.add(model.endpointId);
  aliasSet.add(model.model ?? fallbackModel);
  for (const endpoint of endpoints) {
    aliasSet.add(endpoint.endpointId);
  }
  return {
    id: model.id,
    endpointId: model.endpointId,
    owner: model.owner ?? fallbackOwner,
    model: model.model ?? fallbackModel,
    aliases: Array.from(aliasSet),
    label: model.label,
    description: model.description,
    kind: model.kind,
    category: model.category,
    defaultSettings,
    pricing: model.pricing,
    settings,
    ui,
    rateLimitKey: model.rateLimitKey ?? 'fal:default',
    output: model.output,
    provider: 'fal',
    defaultEndpointKey,
    endpoints,
    fallbackProviders: fallbackProviders.length ? fallbackProviders : undefined
  };
};

const FAL_MODELS: FalModelConfig[] = RAW_MODELS.map(mapFalModel);
const FAL_MODEL_ALIAS_INDEX = new Map<string, FalModelConfig>();
for (const model of FAL_MODELS) {
  for (const alias of model.aliases ?? []) {
    if (!FAL_MODEL_ALIAS_INDEX.has(alias)) {
      FAL_MODEL_ALIAS_INDEX.set(alias, model);
    }
  }
}

export const listFalModels = (): FalModelConfig[] => FAL_MODELS;

export const listFalModelsByKind = (kind: JobKind): FalModelConfig[] =>
  FAL_MODELS.filter((model) => model.kind === kind);

export const getFalModelById = (id: string): FalModelConfig | undefined =>
  FAL_MODELS.find((model) => model.id === id) ?? FAL_MODEL_ALIAS_INDEX.get(id);

export const resolveFalCanonicalModelId = (id: string): string | undefined => {
  const model = getFalModelById(id);
  return model?.id;
};
